import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  TierProgramMatrixRow,
  TierProgramMatrixRowUpdateInput,
} from "@colab/shared";

export type UpdateRowInput = {
  programId: string;
  id: string;
  patch: TierProgramMatrixRowUpdateInput;
};

type UpdateRowResponse = { ok: true; row: TierProgramMatrixRow };

// PATCH /api/admin/tiers/programs/:programId/rows/:id
//
// Pessimistic. Label / description edits are low-frequency; the row_kind
// flip (requirement ↔ benefit) reflows the matrix render and is best left
// to the server refetch for clean state.
export function useUpdateTierRow() {
  const queryClient = useQueryClient();
  return useMutation<UpdateRowResponse, ApiClientError, UpdateRowInput>({
    mutationFn: ({ programId, id, patch }) =>
      apiFetch<UpdateRowResponse>(
        `/admin/tiers/programs/${programId}/rows/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(patch),
        },
      ),
    onSuccess: (_data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(programId) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update row");
    },
  });
}
